import React, { useEffect, useState } from "react";
import { CustomModal, RowAction, AlertTost } from "#components";
import useInfinityGet from "#hooks/useInfinityGet.js";
import usePost from "#hooks/usePost.js";
import usePut from "#hooks/usePut.js";
import useDelete from "#hooks/useDelete.js";
import ENDPOINTS from "#services/endPoints.js";
import QUERY_KEYS from "#services/queryKeys.js";
import Swal from "sweetalert2";
import CustomAlert from "../../../components/CustomAlert copy";

const RateUnitModal = ({ open, onClose }) => {
  const [editUnitId, setEditUnitId] = useState(null);
  const [editedValue, setEditedValue] = useState("");
  const [localUnits, setLocalUnits] = useState([]);

  // Infinite scroll fetch
  const {
    data,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch,
    isLoading: isRateUnitsLoading,
  } = useInfinityGet(
    ENDPOINTS.RATE_UNIT_LIST,
    QUERY_KEYS.RATE_UNIT_LIST_QUERY_KEY
  );

  // POST new rate unit
  const { mutateAsync: addUnitMutateAsync, isLoading: isAdding } = usePost(
    ENDPOINTS.ADD_RATE_UNIT,
    QUERY_KEYS.ADD_RATE_UNIT_QUERY_KEY
  );

  // PUT update rate unit
  const { mutateAsync: updateUnitMutateAsync, isLoading: isUpdating } = usePut(
    "",
    QUERY_KEYS.UPDATE_RATE_UNIT_QUERY_KEY
  );

  // DELETE rate unit
  const { mutateAsync: deleteUnitMutateAsync } = useDelete(
    "",
    QUERY_KEYS.DELETE_RATE_UNIT_QUERY_KEY
  );

  // Sync fetched data into local state
  useEffect(() => {
    if (data?.pages) {
      const allUnits = data.pages.flatMap((page) => page?.data || []);
      setLocalUnits(allUnits);
    }
  }, [data]);

  // Add new rate unit
  const handleAddUnit = async () => {
    const input = document.getElementById("newRateUnitInput");
    const value = input.value.trim();

    if (!value) return AlertTost("Please enter a rate unit", "error");
    if (localUnits.some((u) => u.name === value))
      return AlertTost("Rate unit already exists", "error");

    try {
      const response = await addUnitMutateAsync({ name: value });
      AlertTost(response?.message || "Rate unit added successfully", "success");
      input.value = "";
      await refetch();
    } catch (error) {
      AlertTost(
        error?.response?.data?.message || "Failed to add rate unit",
        "error"
      );
    }
  };

  // Update rate unit
  const handleUpdateUnit = async (unit) => {
    if (!editedValue.trim()) {
      AlertTost("Enter valid rate unit", "error");
      return;
    }
    try {
      const response = await updateUnitMutateAsync({
        _customUrl: ENDPOINTS.UPDATE_RATE_UNIT(unit._id),
        name: editedValue.trim(),
      });
      AlertTost(
        response?.message || "Rate unit updated successfully",
        "success"
      );
      setEditUnitId(null);
      await refetch();
    } catch (error) {
      AlertTost(error?.response?.data?.message || "Failed to update", "error");
    }
  };

  // Delete rate unit
  const handleDeleteUnit = async (unitId) => {
    CustomAlert({
      title: "Are you sure you want to delete this rate unit?",
      confirmButtonText: "Confirm",
      icon: "question",
      onConfirm: async () => {
        try {
          const response = await deleteUnitMutateAsync({
            _customUrl: ENDPOINTS.DELETE_RATE_UNIT(unitId),
          });
          AlertTost(
            response?.message || "Rate unit deleted successfully",
            "success"
          );
          await refetch();
        } catch (error) {
          Swal.fire({
            title: "Error",
            text:
              error?.response?.data?.message || "Failed to delete rate unit",
            icon: "error",
            timer: 2000,
            showConfirmButton: false,
          });
        }
      },
    });
  };

  return (
    <CustomModal
      open={open}
      title="Manage Rate Units"
      onClose={onClose}
      actionButtonTitle="Close"
      onSubmit={onClose}
    >
      <div className="common-modal-form">
        <div className="row gy-3">
          <div className="col-12">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
              <h6
                style={{
                  fontSize: "25px",
                  fontWeight: 500,
                  color: "var(--bs-black)",
                  fontFamily: "var(--primary-font)",
                }}
              >
                Rate Unit Listing
              </h6>

              <div className="d-flex align-items-center flex-wrap gap-2">
                <input
                  type="text"
                  placeholder="Add new rate unit"
                  id="newRateUnitInput"
                  className="form-control"
                  onFocus={() => {
                    if (editUnitId) {
                      setEditUnitId(null);
                      setEditedValue("");
                    }
                  }}
                  style={{
                    width: "200px",
                    padding: "14px",
                    borderRadius: "8px",
                  }}
                />

                <button
                  className="btn primary-btn"
                  onClick={handleAddUnit}
                  disabled={isAdding}
                >
                  {isAdding ? "Adding..." : "Add"}
                </button>
              </div>
            </div>

            {/* Table */}
            <div
              className="card table_card p-0"
              style={{
                maxHeight: "300px",
                overflowY: "auto",
                borderRadius: "10px",
                border: "1px solid #e5e7eb",
              }}
              onScroll={(e) => {
                const { scrollTop, scrollHeight, clientHeight } = e.target;
                if (
                  scrollHeight - scrollTop <= clientHeight + 50 &&
                  hasNextPage &&
                  !isFetchingNextPage
                ) {
                  fetchNextPage();
                }
              }}
            >
              <table className="table table-hover mb-0 align-middle">
                <thead
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    backgroundColor: "#f8f9fa",
                  }}
                >
                  <tr>
                    <th style={{ width: "70%", padding: "15px 30px" }}>
                      Rate Unit
                    </th>
                    <th style={{ width: "30%", padding: "15px 30px" }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {isRateUnitsLoading ? (
                    <tr>
                      <td colSpan={2} className="text-center py-3 text-muted">
                        Loading...
                      </td>
                    </tr>
                  ) : localUnits?.length > 0 ? (
                    localUnits.map((unit, index) => (
                      <tr key={unit._id || index}>
                        {editUnitId === unit._id ? (
                          <>
                            <td style={{ padding: "15px 30px" }}>
                              <input
                                type="text"
                                value={editedValue}
                                onChange={(e) => setEditedValue(e.target.value)}
                                className="form-control"
                              />
                            </td>
                            <td style={{ padding: "15px 30px" }}>
                              <button
                                className="btn primary-btn btn-sm"
                                onClick={() => handleUpdateUnit(unit)}
                                disabled={isUpdating}
                              >
                                Save
                              </button>
                              <button
                                className="btn btn-secondary btn-sm ms-2"
                                onClick={() => setEditUnitId(null)}
                              >
                                Cancel
                              </button>
                            </td>
                          </>
                        ) : (
                          <>
                            <td style={{ padding: "15px 30px" }}>
                              {unit.name}
                            </td>
                            <td
                              className="text-end"
                              style={{ padding: "15px 30px" }}
                            >
                              <RowAction
                                activeAction={{
                                  edit: true,
                                  delete: true,
                                  view: false,
                                  status: false,
                                }}
                                onEdit={() => {
                                  setEditUnitId(unit._id);
                                  setEditedValue(unit.name);
                                }}
                                onDelete={() =>
                                  handleDeleteUnit(unit._id)
                                }
                              />
                            </td>
                          </>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={2} className="text-center py-3 text-muted">
                        No rate units available.
                      </td>
                    </tr>
                  )}
                  {isFetchingNextPage && (
                    <tr>
                      <td colSpan={2} className="text-center py-2 text-muted">
                        Loading more...
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </CustomModal>
  );
};

export default RateUnitModal;
